import { Event } from "../../models/calendar/Event.model";
import Select, { DefaultOptionType } from "antd/es/select";
import { Popover } from "antd";
import { useEffect, useState, useMemo } from "react";
import { formatEventDate } from "../modal/ViewEvent.eventTime";
import { EventService } from "../../services/events.api.client";
import { useConfig } from "../../contexts/ConfigContext";
import { EVENT_STATUS } from "../../constants/eventConstants";



type EventStatus = 'PENDING' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'POSTPONED';

const statusStyles: Record<string, { className: string; label: string }> = {
  PENDING: { className: 'status-pendiente', label: EVENT_STATUS.PENDING.label },
  CONFIRMED: { className: 'status-confirmado', label: EVENT_STATUS.CONFIRMED.label },
  IN_PROGRESS: { className: 'status-progreso', label: EVENT_STATUS.IN_PROGRESS.label },
  COMPLETED: { className: 'status-completo', label: EVENT_STATUS.COMPLETED.label },
  CANCELLED: { className: 'status-cancelado', label: EVENT_STATUS.CANCELLED.label },
  POSTPONED: { className: 'status-propuesto', label: EVENT_STATUS.POSTPONED.label },
};

export default function EventCard ({ event }: Readonly<{event:Event}>) {
    const [style, setStyle] = useState<{ className: string; label: string }>(statusStyles[event.status as EventStatus] || {});
    const {config} = useConfig();
    
    useEffect(() => {
      setStyle(statusStyles[event.status as EventStatus]);
    }, [event.status]);


    const formatString = (value:string)=>{
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }
    
    const updateStatus = (value:string)=>{
      const request = {
        uidCompany:event.uidCompany,
        uidCalendar:event.uidCalendar,
        status:value,
        id:event.id,
        type:event.type,
        creator_enriched: event.creator_enriched
      }
      EventService.updateEvent(request as any, event.uidCompany, event.uidCalendar, config.key).then(()=>{
        setStyle(statusStyles[request.status as EventStatus]);
      }).catch((error_)=>{
        console.error('Error updating event status:', error_);
      })
    }
    
    const items: DefaultOptionType[] = Object.values(EVENT_STATUS).map(s => ({
      value: s.dbValue,
      label: <span>{s.label}</span>,
      className: statusStyles[s.dbValue]?.className
    }));

    const participantNames = useMemo(() => {
        if (event.participants && event.participants.length > 0) {
            return event.participants.map(p => p.name).join(', ');
        }
        if (event.participantIds && event.participantIds.length > 0) {
            return event.participantIds.length > 1 ? `${event.participantIds.length} participantes` : "1 participante";
        }
        return "N/A";
    }, [event.participantIds, event.participants]);

    const popoverContent = (
      <div className="max-w-[320px]">
        <h4 className="font-semibold mb-1">{formatString(event.summary)}</h4>
        <p className="text-gray-500 text-xs mb-3">{formatEventDate(event.start, event.duration, event.end)}</p>
        
        <div className="text-sm border-t border-gray-100 pt-3 flex flex-col gap-1 text-gray-600">
           {event.duration > 0 && <p><span className="font-semibold text-gray-800">Duración:</span> {event.duration} min</p>}
           <p><span className="font-semibold text-gray-800">Participantes:</span> {participantNames}</p>
        </div>
      </div>
    );

    return (
      <div className={`event-card-planner ${style.className}`}>
        <Popover content={popoverContent} title="Detalle del evento" trigger="hover" placement="right">
          <div className="event-details cursor-help w-full pr-4">
            <p className="event-title">{formatString(event.summary)}</p>
            {event.summary && <p className="event-time">{formatEventDate(event.start,event.duration)}</p>}
          </div>
        </Popover>
            <Select placement="bottomLeft" className="w-auto min-w-[130px] ml-auto shrink-0"
                onChange={updateStatus}
                defaultValue={event.status}
                variant="borderless"
                suffixIcon={null}
                popupMatchSelectWidth={false}
                labelRender={(option) => (
                    <span className="event-status-label">
                      {option.label}
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-1 opacity-80">
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                )}>
                    {items.map((item) => (
                        <Select.Option key={item.value} value={item.value} className={item.className}>
                            {item.label}
                        </Select.Option>
                    ))}
            </Select>
      </div>
    );
  };